import {FC} from 'react';

// What the signer service writes when a brand leaves these empty (see the stock Verdocs signature panel).
export const DEFAULT_PDF_REASON = 'Certified By Verdocs';
export const DEFAULT_PDF_LOCATION = 'Verdocs';

const Skeleton: FC<{className: string}> = ({className}) => <div className={`h-1.5 rounded-full bg-gray-200 ${className}`} />;

// Acrobat's signature badge: a pen scribble over a line, with a green check in the corner.
const SignedBadge: FC = () => (
  <svg viewBox="0 0 28 24" className="h-5 w-6 shrink-0" aria-hidden="true">
    <path d="M2 14c2-5 4-9 5-8s-2 8 0 8 3-5 4-5 0 4 2 4 2-2 3-2" fill="none" stroke="#1f2937" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M2 18h13" stroke="#1f2937" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="21" cy="17" r="6" fill="#2e9e44" />
    <path d="M18.2 17.2l2 2 3.6-4" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface CertificationPreviewProps {
  reason: string;
  location: string;
  contact: string;
}

/**
 * The signed PDF as Acrobat shows it for an AATL-listed certificate: the blue "all signatures are valid"
 * bar, then the signature panel (a sidebar in Acrobat, a modal here for room). The Signature Details lines
 * track the form as it is typed, and go bold once they differ from the Verdocs defaults.
 */
export const CertificationPreview: FC<CertificationPreviewProps> = ({reason, location, contact}) => {
  const reasonText = reason.trim() || DEFAULT_PDF_REASON;
  const locationText = location.trim() || DEFAULT_PDF_LOCATION;
  const contactText = contact.trim();
  const custom = (value: string, fallback: string) => !!value.trim() && value.trim() !== fallback;

  return (
    <div>
      <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">In Adobe Acrobat</h4>
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex gap-1.5 border-b border-gray-100 bg-gray-50 px-3 py-2.5">
          <div className="h-2.5 w-2.5 rounded-full bg-gray-300" />
          <div className="h-2.5 w-2.5 rounded-full bg-gray-300" />
          <div className="h-2.5 w-2.5 rounded-full bg-gray-300" />
        </div>

        <div className="flex items-center gap-2 bg-[#cfe2f7] px-3 py-2">
          <SignedBadge />
          <span className="text-[12px] text-gray-900">Signed and all signatures are valid.</span>
        </div>

        <div className="relative bg-gray-100 px-2.5 pb-6 pt-3">
          <div className="absolute inset-x-4 inset-y-4 flex flex-col gap-2.5 bg-white px-5 py-5 opacity-60">
            <Skeleton className="w-1/2" />
            <Skeleton className="w-11/12" />
            <Skeleton className="w-5/6" />
            <Skeleton className="w-full" />
            <Skeleton className="w-2/3" />
            <Skeleton className="mt-6 w-24" />
            <Skeleton className="w-4/5" />
            <Skeleton className="w-11/12" />
            <Skeleton className="w-3/5" />
          </div>

          <div className="relative rounded-lg bg-[#323232] px-3 py-2.5 text-[10px] leading-[1.55] text-gray-300 shadow-xl">
            <div className="text-gray-100">Signature is valid:</div>
            <div className="text-gray-100">Source of Trust obtained from Adobe Approved Trust List (AATL).</div>
            <div className="pl-3">
              <div>Document has not been modified since this signature was applied</div>
              <div>Signer's identity is valid</div>
              <div>The signature includes an embedded timestamp.</div>
              <div>Signature is LTV enabled</div>
            </div>
            <div className="mt-1 text-gray-100">Signature Details</div>
            <div className="pl-3">
              <div className={custom(reason, DEFAULT_PDF_REASON) ? 'font-bold text-white' : ''}>Reason: {reasonText}</div>
              <div className={custom(location, DEFAULT_PDF_LOCATION) ? 'font-bold text-white' : ''}>Location: {locationText}</div>
              {contactText && <div className="font-bold text-white">Contact Info: {contactText}</div>}
              <div>Certificate Details...</div>
            </div>
          </div>
        </div>
      </div>
      <p className="mt-2.5 text-xs leading-relaxed text-gray-500">Updates as you type. Lines in bold differ from the Verdocs defaults.</p>
    </div>
  );
};
